import { PropsWithChildren, ReactNode } from 'react';
import styled from 'styled-components';
import { Flex } from 'reflexbox';

import { toRgbString } from 'packages/kakapo-ui-kit/helpers/HexColor';
import Column from 'packages/kakapo-ui-kit/components/grid/Column';
import Container from 'packages/kakapo-ui-kit/components/grid/Container';

import PopoverProvider from '../popovers/PopoverProvider';

import { ModalPortal } from './ModalProvider';
interface IProps {
	preface?: ReactNode;
	children: ReactNode;

	width?: number;

	// Is used to offset the modal to create effect of depth while stacking
	// multiple modals
	stackIndex?: number;
}

const Element = styled.div<IProps>`
	background-color: ${(props) => props.theme.colors.white};
	margin: 60px 0;

	&:nth-child(2) {
		margin-top: 10px;
	}

	${(props) =>
		props.stackIndex &&
		`
		transform: translateY(${props.stackIndex * 20}px);
	`}
`;

export default function Modal({ children, stackIndex, preface, width }: IProps) {
	return (
		<Container>
			<Flex justifyContent="center">
				<Column width={[1, 1, width ?? 6 / 12]}>
					{preface}
					<Element stackIndex={stackIndex}>{children}</Element>
				</Column>
			</Flex>
		</Container>
	);
}

const ModalBackgroundContainer = styled.div`
	background-color: rgba(${(props) => toRgbString(props.theme.colors.antracite)}, 0.8);
	position: fixed;
	top: 0;
	left: 0;
	z-index: 10;
	width: 100%;
	height: 100%;
	overflow: auto;
`;

export function ModalBackground(props: PropsWithChildren<{}>) {
	return (
		<ModalPortal>
			<ModalBackgroundContainer>{props.children}</ModalBackgroundContainer>
		</ModalPortal>
	);
}

const ColumnWrapper = styled.div<{ backgroundColor?: string }>`
	${(props) =>
		props.backgroundColor ? `background-color: ${props.theme.colors[props.backgroundColor]};` : ''}
`;

interface IModalContentProps {
	children: ReactNode;
	backgroundColor?: string;
	withoutPadding?: boolean;
}

export const ModalContent = ({ children, backgroundColor, withoutPadding }: IModalContentProps) => {
	return (
		<PopoverProvider>
			<ColumnWrapper backgroundColor={backgroundColor}>
				<Column p={withoutPadding ? undefined : [2, 3, 4]}>{children}</Column>
			</ColumnWrapper>
		</PopoverProvider>
	);
};
